import { useEffect, useRef, useState } from 'react'
import { animate, useReducedMotion } from 'framer-motion'
import { DURATION } from './motion'

interface Props {
  value: number
  className?: string
}

/**
 * Counts up to its value when it first appears, and again whenever it changes.
 *
 * Reduced motion is handled by hand here rather than by `MotionConfig`: that
 * setting governs `motion` components, and this is a raw `animate()` call on a
 * number. Someone who has asked for less motion gets the final figure at once.
 */
export function AnimatedNumber({ value, className = '' }: Props) {
  const reduced = useReducedMotion()
  const [display, setDisplay] = useState(reduced ? value : 0)
  const previous = useRef(reduced ? value : 0)

  useEffect(() => {
    if (reduced) {
      setDisplay(value)
      previous.current = value
      return
    }
    const controls = animate(previous.current, value, {
      duration: DURATION.slow * 2,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (latest) => setDisplay(Math.round(latest)),
      onComplete: () => setDisplay(value),
    })
    previous.current = value

    // The count-up is decoration; the number is the point. It is driven by
    // requestAnimationFrame, which a browser is free not to run — a backgrounded
    // tab, a throttled frame loop — and a member would then be looking at a
    // stale 0 where their ascent count should be. Land on the real figure
    // regardless of whether a single frame was ever produced.
    const settle = setTimeout(() => setDisplay(value), DURATION.slow * 2000 + 250)

    return () => {
      controls.stop()
      clearTimeout(settle)
    }
  }, [value, reduced])

  return <span className={className}>{display}</span>
}
